import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../components/ui/sheet';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { toast } from 'sonner';
import { formatRomeDate } from '../lib/datetime';
import {
  Loader2,
  Building2,
  AlertTriangle,
  Flame,
  Filter,
  ChevronRight,
  User as UserIcon,
  ExternalLink,
  BarChart3,
} from 'lucide-react';

const priorityColors = {
  urgente: 'bg-red-600 text-white',
  alta: 'bg-orange-500 text-white',
  media: 'bg-blue-500 text-white',
  bassa: 'bg-slate-400 text-white',
};
const statusLabels = {
  aperto: 'Aperto',
  assegnato: 'Assegnato',
  in_lavorazione: 'In Lavorazione',
  in_attesa_pezzi: 'In Attesa Pezzi',
  completato: 'Completato',
  chiuso: 'Chiuso',
};

const TIME_FILTERS = [
  { value: 7, label: '7gg' },
  { value: 30, label: '30gg' },
  { value: 60, label: '60gg' },
  { value: 90, label: '90gg' },
  { value: 0, label: 'Tutto' },
];

const HeatmapPage = () => {
  const navigate = useNavigate();
  const [hotels, setHotels] = useState([]);
  const [selectedHotel, setSelectedHotel] = useState('');
  const [heatmapData, setHeatmapData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Filtri
  const [daysFilter, setDaysFilter] = useState(60);
  const [onlyOpen, setOnlyOpen] = useState(false);

  // Sheet drill-down
  const [sheetOpen, setSheetOpen] = useState(false);
  const [selectedArea, setSelectedArea] = useState(null);
  const [areaTickets, setAreaTickets] = useState([]);
  const [loadingTickets, setLoadingTickets] = useState(false);

  useEffect(() => {
    const fetchHotels = async () => {
      try {
        const { data } = await api.get('/hotels');
        setHotels(data);
        if (data.length > 0) setSelectedHotel(data[0].id);
      } catch (err) {
        toast.error('Errore nel caricamento hotel');
      } finally {
        setLoading(false);
      }
    };
    fetchHotels();
  }, []);

  const fetchHeatmap = useCallback(async () => {
    if (!selectedHotel) return;
    try {
      setLoading(true);
      const { data } = await api.get(`/heatmap/${selectedHotel}`, {
        params: { days: daysFilter, only_open: onlyOpen },
      });
      setHeatmapData(data);
    } catch (err) {
      toast.error('Errore dati heatmap');
    } finally {
      setLoading(false);
    }
  }, [selectedHotel, daysFilter, onlyOpen]);

  useEffect(() => {
    fetchHeatmap();
  }, [fetchHeatmap]);

  const openAreaSheet = async (areaName) => {
    setSelectedArea(areaName);
    setSheetOpen(true);
    setLoadingTickets(true);
    setAreaTickets([]);
    try {
      const { data } = await api.get(
        `/heatmap/${selectedHotel}/area/${encodeURIComponent(areaName)}/tickets`,
        { params: { days: daysFilter, only_open: onlyOpen } }
      );
      setAreaTickets(data.tickets || []);
    } catch (err) {
      toast.error('Errore nel caricamento ticket area');
    } finally {
      setLoadingTickets(false);
    }
  };

  const getSeverityStyle = (sev) => {
    if (sev === 'red') return 'bg-red-600 text-white';
    if (sev === 'yellow') return 'bg-amber-400 text-amber-900';
    return 'bg-emerald-100 text-emerald-800';
  };

  const getHeatLevel = (d) => {
    if (!d) return 0;
    if (d.total >= 5 || d.urgent >= 2) return 5;
    if (d.total >= 4 || d.urgent >= 1) return 4;
    if (d.total >= 3 || d.open >= 2) return 3;
    if (d.total >= 2) return 2;
    if (d.total >= 1) return 1;
    return 0;
  };

  const RoomCell = ({ roomNumber, detail }) => {
    const heatLevel = getHeatLevel(detail);
    const hasIssues = !!detail && detail.total > 0;
    return (
      <TooltipProvider delayDuration={150}>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={() => hasIssues && openAreaSheet(roomNumber)}
              className={`heatmap-cell heat-${heatLevel} ${hasIssues ? 'is-clickable' : 'cursor-default'}`}
              data-testid={`room-${roomNumber}`}
              disabled={!hasIssues}
              style={!hasIssues ? { cursor: 'default' } : undefined}
            >
              <span>{roomNumber}</span>
              {detail?.urgent > 0 && <span className="urgent-dot" />}
              {detail?.open > 0 && <span className="open-badge">{detail.open}</span>}
            </button>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-[220px]">
            <p className="font-bold">Camera {roomNumber}</p>
            {detail ? (
              <div className="text-xs space-y-1 mt-1">
                <p>Totale: {detail.total} · Aperti: {detail.open} · Chiusi: {detail.closed}</p>
                {detail.urgent > 0 && <p className="text-red-300">Urgenti: {detail.urgent}</p>}
                {detail.top_categories?.length > 0 && (
                  <div className="mt-1 pt-1 border-t border-slate-600">
                    {detail.top_categories.map((c, i) => (
                      <p key={i}>{c.name}: {c.count}</p>
                    ))}
                  </div>
                )}
                <p className="pt-1 text-blue-300 italic">Click per vedere ticket →</p>
              </div>
            ) : (
              <p className="text-xs text-slate-400 mt-1">Nessun ticket nel periodo</p>
            )}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };

  const renderRoomGrid = () => {
    if (!heatmapData) return null;
    const { hotel, area_details, floor_summary } = heatmapData;
    const floors = hotel.floors || 1;
    let maxRooms = 0;
    for (let f = 1; f <= floors; f++) {
      const rooms = floor_summary[f]?.rooms || [];
      if (rooms.length > maxRooms) maxRooms = rooms.length;
    }

    const grid = [];
    for (let floor = floors; floor >= 1; floor--) {
      const floorData = floor_summary[floor] || { total: 0, open: 0, urgent: 0, severity: 'green', rooms: [] };
      const floorRooms = floorData.rooms || [];
      grid.push(
        <div key={floor} className="flex items-center gap-2">
          <TooltipProvider delayDuration={150}>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className={`w-12 shrink-0 text-[10px] font-bold text-center py-2 rounded-md ${getSeverityStyle(floorData.severity)}`}>
                  P.{floor}
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p className="font-bold">Piano {floor}</p>
                <p className="text-xs">Totale: {floorData.total} · Aperti: {floorData.open} · Urgenti: {floorData.urgent}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <div className="flex-1 grid gap-1.5" style={{ gridTemplateColumns: `repeat(${maxRooms}, minmax(36px, 1fr))` }}>
            {floorRooms.map((roomNum) => (
              <RoomCell key={roomNum} roomNumber={roomNum} detail={area_details[roomNum]} />
            ))}
          </div>
        </div>
      );
    }
    return <div className="space-y-2">{grid}</div>;
  };

  const renderCommonAreas = () => {
    if (!heatmapData?.common_areas) return null;
    return (
      <div className="flex flex-wrap gap-2.5">
        {heatmapData.common_areas.map((area) => {
          const heatLevel = getHeatLevel(area);
          const hasIssues = area.total > 0;
          return (
            <TooltipProvider key={area.name} delayDuration={150}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={() => hasIssues && openAreaSheet(area.name)}
                    disabled={!hasIssues}
                    className={`heatmap-cell heat-${heatLevel} ${hasIssues ? 'is-clickable' : 'cursor-default'} py-3 px-4 flex flex-col items-center gap-0.5 min-w-[110px]`}
                    data-testid={`common-area-${area.name.toLowerCase()}`}
                  >
                    <span className="text-[11px] font-bold">{area.name}</span>
                    {area.total > 0 && <span className="text-[9px] opacity-80">{area.total} ticket</span>}
                    {area.urgent > 0 && <span className="urgent-dot" />}
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="font-bold">{area.name}</p>
                  <div className="text-xs space-y-1 mt-1">
                    <p>Totale: {area.total} · Aperti: {area.open}</p>
                    {area.urgent > 0 && <p className="text-red-300">Urgenti: {area.urgent}</p>}
                    {hasIssues && <p className="pt-1 text-blue-300 italic">Click per vedere ticket →</p>}
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          );
        })}
      </div>
    );
  };

  const renderTopCritical = () => {
    if (!heatmapData?.top_critical?.length) return null;
    return (
      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-bold flex items-center gap-2">
            <Flame className="w-5 h-5 text-red-500" />
            Top Aree Critiche
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {heatmapData.top_critical.map((a, idx) => (
              <button
                key={a.area}
                type="button"
                onClick={() => openAreaSheet(a.area)}
                className="w-full flex items-center gap-3 p-3 rounded-lg border border-slate-200 hover:border-blue-500 hover:bg-blue-50 transition-all text-left"
                data-testid={`top-critical-${idx}`}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                  a.severity === 'red' ? 'bg-red-100 text-red-700' :
                  a.severity === 'yellow' ? 'bg-amber-100 text-amber-700' :
                  'bg-emerald-100 text-emerald-700'
                }`}>
                  #{idx + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-slate-900 truncate">{a.area || 'Non specificata'}</p>
                  <div className="flex flex-wrap gap-2 mt-1 text-xs text-slate-500">
                    <span>{a.total} ticket</span>
                    <span>· {a.open} aperti</span>
                    {a.urgent > 0 && <span className="text-red-600 font-semibold">· {a.urgent} urgenti</span>}
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
              </button>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  };

  const renderCategoryBreakdown = () => {
    if (!heatmapData?.category_breakdown?.length) return null;
    const data = heatmapData.category_breakdown.slice(0, 8);
    const max = Math.max(...data.map((c) => c.count), 1);
    return (
      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-bold flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-blue-600" />
            Distribuzione per Categoria
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2.5">
            {data.map((c) => {
              const pct = Math.round((c.count / max) * 100);
              return (
                <div key={c.name} className="flex items-center gap-3" data-testid={`category-${c.name}`}>
                  <div className="w-28 text-xs font-semibold text-slate-700 truncate">{c.name}</div>
                  <div className="flex-1 h-6 bg-slate-100 rounded-md overflow-hidden">
                    <div
                      className="h-full rounded-md bg-gradient-to-r from-blue-600 to-blue-400 transition-all duration-500 flex items-center justify-end px-2"
                      style={{ width: `${pct}%` }}
                    >
                      {pct > 25 && <span className="text-[10px] font-bold text-white">{c.count}</span>}
                    </div>
                  </div>
                  {pct <= 25 && <div className="w-8 text-xs font-bold text-slate-600">{c.count}</div>}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    );
  };

  if (loading && !heatmapData) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-900" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="heatmap-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black text-slate-900">Mappa Problemi</h1>
          <p className="text-slate-500 mt-1">Click su una camera o area comune per vedere i ticket collegati</p>
        </div>
        <Select value={selectedHotel} onValueChange={setSelectedHotel}>
          <SelectTrigger className="w-64" data-testid="hotel-select">
            <SelectValue placeholder="Seleziona hotel" />
          </SelectTrigger>
          <SelectContent>
            {hotels.map((h) => (
              <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Filtri */}
      <Card className="border-slate-200">
        <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex items-center gap-2 text-sm text-slate-600 font-semibold">
            <Filter className="w-4 h-4" />
            Periodo:
          </div>
          <div className="flex flex-wrap gap-2">
            {TIME_FILTERS.map((tf) => (
              <button
                key={tf.value}
                type="button"
                onClick={() => setDaysFilter(tf.value)}
                className={`pill-filter ${daysFilter === tf.value ? 'pill-filter-active' : 'pill-filter-default'}`}
                data-testid={`filter-days-${tf.value}`}
              >
                {tf.label}
              </button>
            ))}
          </div>
          <div className="sm:ml-auto">
            <label className="flex items-center gap-2 cursor-pointer text-sm" data-testid="filter-only-open">
              <input
                type="checkbox"
                checked={onlyOpen}
                onChange={(e) => setOnlyOpen(e.target.checked)}
                className="rounded border-slate-300"
              />
              <span className="font-semibold text-slate-700">Solo ticket aperti</span>
            </label>
          </div>
        </CardContent>
      </Card>

      {heatmapData && (
        <>
          {/* KPI Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Card className="stat-card" data-testid="stat-hotel-name">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="min-w-0">
                    <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Hotel</p>
                    <p className="text-lg font-black text-slate-900 mt-1 truncate">{heatmapData.hotel.name}</p>
                  </div>
                  <Building2 className="w-8 h-8 text-blue-200 flex-shrink-0" />
                </div>
              </CardContent>
            </Card>
            <Card className="stat-card">
              <CardContent className="p-4">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Piani · Camere</p>
                <p className="text-2xl font-black text-slate-900 mt-1">
                  {heatmapData.hotel.floors} · {heatmapData.hotel.rooms?.length || heatmapData.hotel.num_rooms}
                </p>
              </CardContent>
            </Card>
            <Card className="stat-card">
              <CardContent className="p-4">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Aree con Ticket</p>
                <p className="text-2xl font-black text-amber-600 mt-1">{heatmapData.total_areas_with_issues}</p>
              </CardContent>
            </Card>
            <Card className="stat-card">
              <CardContent className="p-4">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Ticket Urgenti</p>
                <p className="text-2xl font-black text-red-600 mt-1">
                  {Object.values(heatmapData.area_details).reduce((s, a) => s + (a.urgent || 0), 0)}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Top Critical + Category Breakdown side-by-side */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {renderTopCritical()}
            {renderCategoryBreakdown()}
          </div>

          {/* Heatmap Camere */}
          <Card className="border-slate-200">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <CardTitle className="text-lg font-bold">Heatmap Camere</CardTitle>
                <div className="flex items-center gap-2 text-[10px]">
                  <span className="text-slate-400">Severità:</span>
                  {[0, 1, 2, 3, 4, 5].map((l) => (
                    <div key={l} className="flex items-center gap-0.5">
                      <div className={`w-4 h-4 rounded heat-${l}`} />
                      <span className="text-slate-400">{l === 0 ? 'OK' : l === 5 ? '5+' : l}</span>
                    </div>
                  ))}
                </div>
              </div>
            </CardHeader>
            <CardContent className="px-4 pb-4">
              <div className="overflow-y-auto border border-slate-100 rounded-lg p-3 bg-slate-50/50" style={{ maxHeight: '460px' }}>
                {renderRoomGrid()}
              </div>
            </CardContent>
          </Card>

          {/* Aree Comuni */}
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg font-bold">Aree Comuni</CardTitle>
            </CardHeader>
            <CardContent>{renderCommonAreas()}</CardContent>
          </Card>

          {/* Floor Summary */}
          <Card className="border-slate-200" data-testid="floor-summary">
            <CardHeader>
              <CardTitle className="text-lg font-bold">Riepilogo per Piano</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {Object.entries(heatmapData.floor_summary || {}).map(([floor, data]) => (
                  <div
                    key={floor}
                    className={`p-4 rounded-lg border-2 ${
                      data.severity === 'red' ? 'border-red-300 bg-red-50' :
                      data.severity === 'yellow' ? 'border-amber-300 bg-amber-50' :
                      'border-green-300 bg-green-50'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <p className="font-bold text-slate-900">Piano {floor}</p>
                      <Badge className={
                        data.severity === 'red' ? 'bg-red-600 text-white' :
                        data.severity === 'yellow' ? 'bg-amber-500 text-white' :
                        'bg-green-600 text-white'
                      }>
                        {data.severity === 'red' ? 'Critico' : data.severity === 'yellow' ? 'Attenzione' : 'OK'}
                      </Badge>
                    </div>
                    <div className="flex gap-4 mt-2 text-sm text-slate-600">
                      <span>Totale: {data.total}</span>
                      <span>Aperti: {data.open}</span>
                      {data.urgent > 0 && <span className="text-red-600 font-medium">Urgenti: {data.urgent}</span>}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Drill-down Sheet */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto" data-testid="area-detail-sheet">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              {selectedArea ? `Area: ${selectedArea}` : 'Dettaglio Area'}
            </SheetTitle>
            <SheetDescription>
              {loadingTickets
                ? 'Caricamento ticket...'
                : `${areaTickets.length} ticket trovati nel periodo selezionato`}
            </SheetDescription>
          </SheetHeader>

          <div className="mt-6 space-y-3">
            {loadingTickets ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-blue-900" />
              </div>
            ) : areaTickets.length === 0 ? (
              <div className="text-center py-12 text-slate-500 text-sm">
                Nessun ticket per questa area nel periodo selezionato.
              </div>
            ) : (
              <>
                {areaTickets.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => {
                      setSheetOpen(false);
                      navigate(`/tickets/${t.id}`);
                    }}
                    className="w-full text-left p-3 rounded-lg border border-slate-200 hover:border-blue-500 hover:bg-blue-50 transition-all"
                    data-testid={`area-ticket-${t.id}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 mb-1.5">
                          <Badge className={`text-[10px] ${priorityColors[t.priority] || 'bg-slate-400 text-white'}`}>
                            {t.priority}
                          </Badge>
                          <Badge variant="secondary" className="text-[10px]">
                            {statusLabels[t.status] || t.status}
                          </Badge>
                        </div>
                        <p className="font-semibold text-slate-900 text-sm truncate">{t.title}</p>
                        <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1.5 text-[11px] text-slate-500">
                          {t.category_name && <span>📂 {t.category_name}</span>}
                          {t.created_at && <span>📅 {formatRomeDate(t.created_at)}</span>}
                          {t.assigned_to_name ? (
                            <span className="flex items-center gap-0.5">
                              <UserIcon className="w-3 h-3" />
                              {t.assigned_to_name}
                            </span>
                          ) : (
                            <span className="text-amber-600">Da assegnare</span>
                          )}
                          {t.operator_signature && (
                            <span className="text-slate-600">Firma: {t.operator_signature}</span>
                          )}
                        </div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0 mt-1" />
                    </div>
                  </button>
                ))}

                <div className="pt-3 border-t border-slate-200">
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    onClick={() => {
                      setSheetOpen(false);
                      navigate(`/tickets?hotel=${selectedHotel}`);
                    }}
                    data-testid="open-tickets-page-btn"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Vai a Tutti i Ticket dell'hotel
                  </Button>
                </div>
              </>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default HeatmapPage;
